import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Loader2, GripVertical } from "lucide-react";
import { authApi, resolveImageUrl } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageFitControls from "@/components/admin/ImageFitControls";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const emptyBanner = { image: "", kicker: "", heading: "", subtext: "", button_label: "", button_link: "/shop", order: 0, active: true, image_fit: "cover", image_position: "center" };

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // banner id or "new"
  const [form, setForm] = useState(emptyBanner);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    authApi.get("/admin/banners").then(({ data }) => { setBanners(data); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...emptyBanner, order: banners.length }); setOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ ...b }); setOpen(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("image", resolveImageUrl(data.path));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.image) { toast.error("Please upload a banner image"); return; }
    const payload = { ...form, order: parseInt(form.order, 10) || 0 };
    setSaving(true);
    try {
      if (editing === "new") await authApi.post("/admin/banners", payload);
      else await authApi.put(`/admin/banners/${editing}`, payload);
      toast.success("Banner saved");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (b) => {
    await authApi.put(`/admin/banners/${b.id}`, { ...b, active: !b.active });
    setBanners((arr) => arr.map((x) => x.id === b.id ? { ...x, active: !x.active } : x));
  };

  const del = async (id) => {
    await authApi.delete(`/admin/banners/${id}`);
    toast.success("Banner deleted");
    setBanners((arr) => arr.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="admin-banners-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-2">Hero Banners</h1>
          <p className="text-muted-foreground text-sm mt-1">Rotating carousel on the homepage · lower order shows first</p>
        </div>
        <button onClick={openNew} className="bg-[#E31837] text-white px-6 py-3 label-caps flex items-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-95" data-testid="add-banner-btn">
          <Plus className="h-4 w-4" /> Add Banner
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Loading…</div>
      ) : banners.length === 0 ? (
        <div className="bg-white border border-border p-10 text-center text-muted-foreground">No banners yet. Add your first one.</div>
      ) : (
        <div className="space-y-3">
          {banners.map((b) => (
            <div key={b.id} className="bg-white border border-border flex items-center gap-4 p-3" data-testid={`banner-row-${b.id.slice(0, 8)}`}>
              <GripVertical className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="w-8 text-center text-sm font-bold text-muted-foreground">{b.order}</span>
              <div className="w-28 h-16 bg-secondary border border-border overflow-hidden shrink-0">
                {b.image && <img src={b.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-head font-bold uppercase tracking-tight text-lg leading-none truncate">{(b.heading || "Untitled").replace(/\n/g, " ")}</p>
                <p className="text-xs text-muted-foreground mt-1 truncate">{b.kicker} · → {b.button_link}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs label-caps text-muted-foreground">{b.active ? "Live" : "Hidden"}</span>
                <Switch checked={b.active} onCheckedChange={() => toggleActive(b)} data-testid={`banner-toggle-${b.id.slice(0, 8)}`} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(b)} className="p-2 hover:text-[#E31837] btn-transition" data-testid={`edit-banner-${b.id.slice(0, 8)}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="p-2 hover:text-[#E31837] btn-transition" aria-label="Delete" data-testid={`delete-banner-${b.id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
                      <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(b.id)} className="rounded-none bg-[#E31837] hover:bg-[#b3122c]">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-auto" data-testid="banner-dialog">
          <DialogHeader><DialogTitle className="heading-3">{editing === "new" ? "Add Banner" : "Edit Banner"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-caps text-muted-foreground">Banner Image (wide)</Label>
              <div className="mt-2 h-40 bg-secondary border border-border overflow-hidden flex items-center justify-center">
                {form.image ? <img src={form.image} alt="preview" className="w-full h-full" style={{ objectFit: form.image_fit || "cover", objectPosition: form.image_position || "center" }} data-testid="banner-image-preview" /> : <span className="text-muted-foreground text-sm">No image</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} data-testid="banner-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full mt-2 border border-[#0A0A0A] py-2.5 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid="banner-upload-btn">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Upload</>}
              </button>
              <div className="mt-3">
                <ImageFitControls fit={form.image_fit} position={form.image_position} onChange={(p) => setForm((f) => ({ ...f, ...p }))} testidPrefix="banner-image" />
              </div>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Kicker (small label)</Label>
              <Input value={form.kicker} onChange={(e) => setField("kicker", e.target.value)} className="mt-2 rounded-none" data-testid="banner-kicker" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Heading (Enter = new line)</Label>
              <Textarea rows={2} value={form.heading} onChange={(e) => setField("heading", e.target.value)} className="mt-2 rounded-none" data-testid="banner-heading" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Subtext</Label>
              <Textarea rows={2} value={form.subtext} onChange={(e) => setField("subtext", e.target.value)} className="mt-2 rounded-none" data-testid="banner-subtext" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps text-muted-foreground">Button Label</Label>
                <Input value={form.button_label} onChange={(e) => setField("button_label", e.target.value)} className="mt-2 rounded-none" data-testid="banner-button-label" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Button Link</Label>
                <Input value={form.button_link} onChange={(e) => setField("button_link", e.target.value)} className="mt-2 rounded-none" placeholder="/shop" data-testid="banner-button-link" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="label-caps text-muted-foreground">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} className="mt-2 rounded-none" data-testid="banner-order" />
              </div>
              <div className="flex items-center justify-between border border-border p-3">
                <Label className="label-caps">Live</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setField("active", v)} data-testid="banner-active" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} className="bg-[#E31837] text-white px-6 py-3 label-caps btn-transition hover:bg-[#b3122c] disabled:opacity-60" data-testid="save-banner-btn">
              {saving ? "Saving…" : "Save Banner"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
